import { CHARGINGPOINT_CATEGORY } from '@/../shared/valueholders/chargingpoint-statuses'
import { Bugfender } from '@bugfender/sdk'
import Vue from 'vue'
import _ from 'lodash'
import { getLatestCoordinates } from '@/helpers/chargingpoints'
import { SOURCE, SOURCES_HIDDEN_BY_DEFAULT } from '@/services/sourceTranslation'

const SOURCE_VISIBILITY_STORAGE_KEY = 'evmaps-locationSources-visibility'
const SOURCE_VISIBILITY_STORAGE_VERSION = 2

const getSourceType = chargingpoint => chargingpoint.dataSource?.value || 'unknown'

/**
 * Replace a chargingpoint by uuid, or add it when it is not in the list yet
 */
const upsertByUuid = (chargingpoints, chargingpoint) => {
  const index = chargingpoints.findIndex(cp => cp.uuid === chargingpoint.uuid)

  if (index === -1) {
    chargingpoints.push(chargingpoint)
  } else {
    chargingpoints.splice(index, 1, chargingpoint)
  }
}

/**
 * The location source visibility choices the user made, by source type
 */
const getStoredSourceVisibility = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SOURCE_VISIBILITY_STORAGE_KEY))
    return stored?.version === SOURCE_VISIBILITY_STORAGE_VERSION ? stored.sources : {}
  } catch {
    return {}
  }
}

const storeSourceVisibility = (sources) => {
  try {
    localStorage.setItem(SOURCE_VISIBILITY_STORAGE_KEY, JSON.stringify({
      version: SOURCE_VISIBILITY_STORAGE_VERSION,
      sources: { ...getStoredSourceVisibility(), ...sources },
    }))
  } catch {
    // The choice is not remembered, but still applied
  }
}

const state = {
  active: false,
  selectedChargingPointID: null,

  /**
   * Phases, that are added here, are locked and need to be unlocked by holding or extra clicking
   */
  lockedPhases: [],

  /**
   * helper alternative locations
   */
  rejectedLocationUuid: null,
  isPlanningAlternativeLocation: false,
  // for all locations that have been prompted
  flaggedLocations: new Set(),

  /**
   * All lines that should be displayed on the map.
   *
   * Lines are configurable
   *
   * @param {array} fromCoordinates
   * @param {array} toPoints
   * @param {boolean} [dashedLine] - false
   */
  mapLines: [],

  /**
   * All charging points
   */
  chargingpoints: [],
  allCPOS: [],
  initialChargingpoints: [],
  chargingPointsByValidator: [],
  /**
   * There is a layer per charging point type
   */
  layers: null,

  /*
  * All comments (used for search and filters)
  */
  allComments: [],

  /*
  * Comments by chargingpoint uuid (used for validation count indicator)
  */
  // comments: [],
  hasLoadedChargingPoints: false,
  hasLoadedComments: false,
  /*
  * All participation comments
  */
  participationComments: [],
  // ids of the selected parking lots
  selectedParkingLotIds: [],
  // ids of the results of the search
  highlightedParkingLots: [],
  locationSources: [],
  tempChargerPopupFields: null,
}

const getters = {
  isActive: state => !!state.active,
  isChargingpointSelected: state => state.selectedChargingPointID !== null,
  getSelectedChargingpointID: state => state.selectedChargingPointID,
  getSelectedChargingpoint: state => state.chargingpoints.find(chargingpoint => chargingpoint._id === state.selectedChargingPointID),
  getSelectedChargingpointUuid: (state, getters) => getters['getSelectedChargingpoint']?.uuid,
  hasLoadedChargingPoints: (state) => state.hasLoadedChargingPoints,
  hasLoadedComments: (state) => state.hasLoadedComments,
  getChargingPoints: state => state.chargingpoints.filter(chargingpoint => !chargingpoint.deleted_at && !chargingpoint.merged_at),
  getChargingPointByRefId: state => ({ _id }) => state.chargingpoints.find(chargingpoint => chargingpoint._id === _id),
  getChargingPointByUuid: state => ({ uuid }) => state.chargingpoints.find(chargingpoint => chargingpoint.uuid === uuid),
  getLockedPhases: state => state.lockedPhases,
  getPlannedChargingPoints: state => state.chargingpoints.filter(chargingpoint => CHARGINGPOINT_CATEGORY.PLANNED.includes(chargingpoint.properties.status)),
  getLocationSources: state => state.locationSources.filter(source => source.count),
  getTempChargerPopupFields: state => state.tempChargerPopupFields,

  /**
   * Used for the chargingpoint list in PlanModeSearch (search/filter sidebar left) and ChargerPointLayer (map)
   *
   * @return {array} - chargingpoints
   */
  getFilteredChargingpoints: (_, getters, rootState, rootGetters) => {
    if (!rootGetters['filters/getActiveFilters']) {
      return getters['getChargingpoints']
    }

    // prefilter statuses are used to filter the locations, before running the filter functions on them.
    // F.e. monitoring data is only available for realized locations, so we can filter out all other locations.
    const prefilter = rootGetters['filters/getActiveFilters'].flatMap(filter => filter.statuses ?? [])

    return rootGetters['filters/getActiveFilters']
      .reduce(
        // run all filter functions on given chargingpoints
        (chargingpoints, filter) => filter.filterFn({ chargingpoints, filter }),
        // start with only the chargingpoints that are visible on the map
        getters['getChargingPoints']
          .filter(chargingpoint =>
            // filter locations by prefilter, if active
            (prefilter.length > 0 ? prefilter.includes(chargingpoint.properties.status) : true) &&
            // only show chargingpoints that are visible on the map
            getters['getPlanmodeLayers']?.some(layer => layer.id === `chargingpoints-${chargingpoint.properties.status}` && layer.visible)),
      )
  },
  getChargingPointIndexByRefId: state => ({ _id }) => {
    return state.chargingpoints.findIndex(chargingpoint => chargingpoint._id === _id)
  },

  getSuccessorsByUuid: (state, getters) => ({ uuid }) => {
    const needle = getters['getChargingPointByUuid']({ uuid })
    if (!needle) return []

    return getters['getChargingPoints']
      .filter(chargingpoint => {
        const { predecessor } = chargingpoint.properties

        if (!predecessor) return false
        if (predecessor.uuid !== needle.uuid) return false

        return true
      })
  },

  getPredecessorsByUuid: (state, getters) => ({ uuid }) => {
    const predecessor = getters['getChargingPointByUuid']({ uuid })

    if (!predecessor) {
      return []
    }

    let more = []
    if (predecessor?.properties.predecessor) {
      more = getters['getPredecessorsByUuid']({ uuid: predecessor?.properties.predecessor.uuid })
    }

    return [predecessor].concat(more)
  },
  getAllAlternativeLocationsByUuid: (state, getters) => ({ uuid }) => {
    const successors = getters['getSuccessorsByUuid']({ uuid })

    if (!successors.length) return []

    const more = successors.flatMap(successor => getters['getAllAlternativeLocationsByUuid']({ uuid: successor.uuid }))

    return successors.concat(more)
  },

  getRejectedLocationUuid: (state) => state.rejectedLocationUuid,
  isPlanningAlternativeLocation: (state) => state.isPlanningAlternativeLocation,
  getFlaggedLocations: (state) => state.flaggedLocations,

  /**
   * compute the lines to all successors, starting at uuid (origin) recursively
   */
  getLinesToSuccessorsRecursiveByUuid: (state, getters) => ({ uuid }) => {
    const chargingpoint = getters['getChargingPointByUuid']({ uuid })
    const successors = getters['getSuccessorsByUuid']({ uuid })

    if (!successors.length) return []

    const more = successors.flatMap(successor => getters['getLinesToSuccessorsRecursiveByUuid']({ uuid: successor.uuid }))

    return [{
      fromCoordinates: getLatestCoordinates({ chargingpoint }),
      toPoints: successors.map(point => getLatestCoordinates({ chargingpoint: point })),
      dashedLine: true,
    }].concat(more)
  },

  /**
   * get all lines that should be drawn to the map
   */
  getMapLines: state => state.mapLines,

  /**
   * Chargingpoint layer state
   */
  getPlanmodeLayers: state => state.layers,


  getCommentIndexByRefId: state => ({ _id }) => state.allComments.findIndex(comment => comment._id === _id),
  getAllCommentsByCurrentUser: (state, getters, rootState, rootGetters) => state.allComments.filter(comment => comment.user?.id === rootGetters['currentUser/getCurrentUserId']),

  getAllCommentsByUser: (state) => (userId) => state.allComments.filter(comment => comment.user?.id === userId),

  hasUserAlreadyVoted: (state, getters) => ({ chargingpointUuid, userId }) => !!getters['getAllCommentsByUser'](userId)
    .filter(comment => comment.chargingpointUuid === chargingpointUuid && !!comment?.validation && !comment?.deleted_at).length,

  getCommentsBySelectedChargingpoint: (state, getters) => state.allComments.filter(comment => comment.chargingpointUuid === getters['getSelectedChargingpointUuid']),

  /**
   * Participation comments
   */
  getParticipationComments: (state) => {
    const chargingpoints = state.initialChargingpoints

    return state.participationComments?.filter(comment => {
      const isNotDeletedChargingpoint = !!chargingpoints.find(cp => cp.uuid === comment.chargingpointUuid)
      return isNotDeletedChargingpoint && !comment.deleted_at
    })
  },
  getParticipationCommentIndexByRefId: state => ({ _id }) => state.participationComments.findIndex(comment => comment._id === _id),
  getParticipationCommentsByChargingpointUuid: (state) => ({ chargingpointUuid }) => state.participationComments.filter((comment) => comment.chargingpointUuid === chargingpointUuid),

  /**
   * Possible duplicates
   */
  getPossibleDuplicatesOfSelectedChargingpoint: (state, getters) => {
    const selectedChargingpoint = getters['getSelectedChargingpoint']
    if (!selectedChargingpoint) return []

    const selectedIsEM = selectedChargingpoint.dataSource?.value === SOURCE.ECO_MOVEMENT

    const possibleDuplicateOf = selectedChargingpoint?.possibleDuplicates?.flatMap((uuid) => getters['getChargingPointByUuid']({ uuid }) ?? []) ?? []

    const allChargingpoints = getters['getChargingPoints']
    const possibleDuplicatedBy = allChargingpoints.filter((cp) => cp?.possibleDuplicates?.includes(selectedChargingpoint.uuid))

    let duplicates = possibleDuplicateOf.concat(possibleDuplicatedBy)

    if (selectedIsEM) {
      duplicates = duplicates.filter(cp => cp.dataSource?.value !== SOURCE.ECO_MOVEMENT)
    }

    const visibleUuids = new Set(
      getters['getFilteredChargingpoints'].map((cp) => cp.uuid),
    )

    return duplicates.filter((cp) => visibleUuids.has(cp.uuid))
  },
  getSelectedParkingLotIds: (state) => state.selectedParkingLotIds,
  getHighlightedParkingLots: (state) => state.highlightedParkingLots,
}
const actions = {
  setTempChargerPopupFields({ commit }, { fields }) {
    commit('setTempChargerPopupFields', { fields })
  },
  resetChargingpoints({ commit }) {
    commit('resetChargingpoints')
  },
  setLoadingState({ commit }, { value }) {
    commit('setLoadingState', { value })
  },
  setLoadingStateComments({ commit }, { value }) {
    commit('setLoadingStateComments', { value })
  },
  // Sinds aug. 2024, some municipalities with Volt import, need to have locking configurable //
  // If a prop. isVoltLocked is not present within a config object, Volt is locked. //
  // If isVoltLocked is present, than the choice (toggle value) will be used //
  setEditabilityBySource({ commit }, { isLocked, source }) {
    commit('setEditabilityBySource', { isLocked, source })
  },
  setLocationSourcesVisibility({ commit }, { value }) {
    commit('setLocationSourcesVisibility', { value })
  },
  filterChargingPointsBySource({ commit }, { value, source }) {
    commit('filterChargingPointsBySource', { value, source })
  },
  /**
   * Add or update a chargingpoint that was saved to the Data Storage after the initial load event
   *  This may be triggered due to an action by the current user, or by another user through a Pusher event
   */
  addOrUpdateChargingPoint({ commit, getters, rootGetters }, { chargingpoint }) {
    if (!chargingpoint) return

    // Ignore actions that relate to chargingpoints from a municipality that is no longer active
    if (rootGetters['access/getActiveMunicipality'] !== chargingpoint.code) return

    const _id = chargingpoint._id
    const index = getters.getChargingPointIndexByRefId({ _id })

    if (index !== -1) {
      commit('updateChargingpoint', { chargingpoint, index })
    } else {
      commit('addChargingpoint', { chargingpoint })
    }
  },
  mergeChargingpoints({ commit, dispatch }, { chargingpoints, result }) {
    dispatch('deselectChargingPoint')
    const toBeDeleted = chargingpoints.find(uuid => uuid !== result.uuid)
    commit('deleteChargingpoint', { chargingpointUuid: toBeDeleted })
    dispatch('addOrUpdateChargingPoint', { chargingpoint: result })
  },
  removePossibleDuplicates({ dispatch, getters }, { uuid1, uuid2 }) {
    const chargingpoint1 = getters['getChargingPointByUuid']({ uuid: uuid1 })
    chargingpoint1.possibleDuplicates = chargingpoint1.possibleDuplicates?.filter(uuid => uuid !== uuid2) ?? []
    dispatch('addOrUpdateChargingPoint', { chargingpoint: chargingpoint1 })

    const chargingpoint2 = getters['getChargingPointByUuid']({ uuid: uuid2 })
    chargingpoint2.possibleDuplicates = chargingpoint2.possibleDuplicates?.filter(uuid => uuid !== uuid1) ?? []
    dispatch('addOrUpdateChargingPoint', { chargingpoint: chargingpoint2 })
  },
  // eslint-disable-next-line no-unused-vars
  async fetchCommentsByChargingpointUuid({ _, dispatch, rootGetters }, { chargingpointUuid }) {
    const code = rootGetters['access/getActiveMunicipality']
    const token = await this.$auth.getTokenSilently()

    try {
      const api = await fetch('/api/commentsload', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ code, chargingpointUuid }),
      })

      const { comments } = await api.json()
      const response = JSON.parse(comments)

      if (!response) {
        return
      }

      dispatch('addOrUpdateComments', ({ comments: response }))
    } catch (error) {
      Bugfender.error('fetchCommentsByChargingpointUuid: ', error)

      Vue.notify({
        type: 'error',
        title: 'Er is iets misgegaan!',
        text: 'De opmerkingen voor deze locatie konden niet worden geladen',
      })
    }
  },

  async getValidationCommentsByUserID({ state, commit, dispatch }, { code, after }) {
    const token = await this.$auth.getTokenSilently()
    const userId = await this.$auth.user.sub
    const api = await fetch('/api/commentsload', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ code, after, userId }),
    })

    if (!api.ok) {
      return Vue.notify({
        type: 'error',
        title: 'Er is iets misgegaan!',
        text: 'De opmerkingen konden niet worden geladen',
      })
    }

    const result = await api.json()
    const comments = JSON.parse(result.comments)

    state.allComments = [...state.allComments, ...comments]
    commit('setAllComments', ({ comments }))

    if (result.after) {
      return await dispatch('getValidationCommentsByUserID', { code, after: result.after })
    }

    commit('setLoadingStateComments', ({ value: true }))
  },
  // eslint-disable-next-line no-unused-vars
  async getStakeholderByUUID({ commit }, { uuid }) {
    const token = await this.$auth.getTokenSilently()

    try {
      const api = await fetch('/api/get-stakeholder-by-uuid', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ uuid }),
      })

      const response = await api.json()

      return response
    } catch (error) {
      console.log(error)
    }
  },

  async fetchChargingPointsByValidator({ commit }, { user_id }) {
    const token = await this.$auth.getTokenSilently()

    try {
      const response = await fetch('/api/chargingpoints-by-validator', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ user_id }),
      })

      if (!response) {
        throw new Error('No response returned')
      }
      commit('setChargingPointsByValidator', { chargingpoints: response })
      return response
    } catch (error) {
      Bugfender.error(error)
      Vue.notify({
        type: 'error',
        title: 'Er is iets misgegaan!',
        text: 'De laadpalen konden niet worden geladen',
      })
    }
  },
  addOrUpdateComments({ commit }, { comments }) {
    commit('updateCommentsAsNeeded', { comments })
  },

  deleteComment({ commit, getters }, { comment }) {
    let _id = comment._id
    let index = getters.getCommentIndexByRefId({ _id })
    commit('updateComment', { comment: comment, index })
  },

  async fetchAllParticipationComments({ dispatch }, { code }) {
    const token = await this.$auth.getTokenSilently()

    const api = await fetch('/api/participationcommentsload', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ code }),
    })

    if (api.ok) {
      const response = await api.json()
      const { comments } = response
      if (comments) {
        comments.forEach(comment => dispatch('addOrUpdateParticipationComment', ({ comment })))
        return comments
      }
    }

    return Vue.notify({
      type: 'error',
      title: 'Er is iets misgegaan!',
      text: 'De opmerkingen konden niet worden geladen',
    })
  },
  addOrUpdateParticipationComment({ commit, getters }, { comment }) {
    let _id = comment._id
    let index = getters.getParticipationCommentIndexByRefId({ _id })

    if (index !== -1) {
      commit('updateParticipationComment', { comment, index })
    } else {
      commit('addParticipationComment', { comment })
    }
  },

  selectChargingPoint({ dispatch, commit, getters }, { _id }) {
    commit('setSelectedChargingPoint', { _id })
    dispatch('showLinesToAlternativeLocationsByUuid', ({ uuid: getters['getSelectedChargingpoint'].uuid }))
    dispatch('showLinesToPossibleDuplicates') // TODO: distinguish lines from alternative locations
  },
  deselectChargingPoint({ commit, dispatch }) {
    commit('setSelectedChargingPoint', { _id: null })
    dispatch('hideLinesToAlternativeLocationsByUuid')
    dispatch('hideLinesToPossibleDuplicates')
  },

  /*
  * LAYERS
  */
  updatePlanmodeLayer({ commit }, { value, layer }) {
    commit('updatePlanmodeLayerVisibility', { value, id: layer.id })
  },

  /**
   * COMMANDS
   */
  activatePlanmode({ commit }) {
    commit('setActiveState', { active: true })
  },
  planAlternativeLocationForUuid({ commit, dispatch }, { uuid }) {
    commit('setRejectedLocationUuid', { uuid })
    dispatch('showLinesToAlternativeLocationsByUuid', { uuid })
  },
  clearAlternativeLocationForUuid({ commit }) {
    commit('setRejectedLocationUuid', { uuid: null })
  },
  showAlternativeLocations({ commit }) {
    commit('setIsPlanningAlternativeLocation', { active: true })
  },
  hideAlternativeLocations({ commit }) {
    commit('setIsPlanningAlternativeLocation', { active: false })
  },
  showLinesToAlternativeLocationsByUuid({ commit, getters }, { uuid }) {
    if (!uuid) return

    const origin = getters['getPredecessorsByUuid']({ uuid }).slice().pop() ?? getters['getSelectedChargingpoint']
    const lines = getters['getLinesToSuccessorsRecursiveByUuid']({ uuid: origin.uuid })
    commit('setMapLinesById', { id: 'alternate-locations', lines })
  },
  hideLinesToAlternativeLocationsByUuid({ commit }) {
    commit('setMapLinesById', { id: 'alternate-locations', lines: [] })
  },
  showLinesToPossibleDuplicates({ commit, getters }) {
    const selectedChargingpoint = getters['getSelectedChargingpoint']
    const possibleDuplicates = getters['getPossibleDuplicatesOfSelectedChargingpoint']

    const lines = [{
      fromCoordinates: getLatestCoordinates({ chargingpoint: selectedChargingpoint }),
      toPoints: possibleDuplicates.map(point => getLatestCoordinates({ chargingpoint: point })),
      dashedLine: true,
    }]
    commit('setMapLinesById', { id: 'possible-duplicates', lines })
  },
  hideLinesToPossibleDuplicates({ commit }) {
    commit('setMapLinesById', { id: 'possible-duplicates', lines: [] })
  },
  addToFlaggedLocations({ commit }, { chargingpoints }) {
    chargingpoints.forEach(chargingpoint => commit('addFlaggedLocation', { uuid: chargingpoint.uuid }))
  },
  setParkingLotIds({ commit }, { ids }) {
    commit('setParkingLotIds', { ids: ids.filter(Boolean) })
  },
  setHighlightedParkingLots({ commit }, { ids }) {
    commit('setHighlightedParkingLots', { ids: ids.filter(Boolean) })
  },
}
const mutations = {
  setTempChargerPopupFields(state, { fields }) {
    state.tempChargerPopupFields = fields
  },
  resetChargingpoints(state) {
    state.chargingpoints = state.initialChargingpoints = []
  },
  setEditabilityBySource(state, { isLocked, source }) {
    state.chargingpoints.map(chargingpoint => {
      const isVattenfallProcess = chargingpoint.isVattenfallProcess
      if (chargingpoint.dataSource?.value === source && !isVattenfallProcess) {
        chargingpoint.isLockedForEditing = isLocked
      }
    })
  },
  setLocationSourcesVisibility(state, { value }) {
    state.locationSources.forEach(source => source.visibility = value)
    state.chargingpoints = value ? state.initialChargingpoints : []

    storeSourceVisibility(Object.fromEntries(state.locationSources.map(source => [source.type, value])))
  },
  setChargingPointsByValidator(state, { chargingpoints }) {
    state.chargingPointsByValidator = chargingpoints
  },
  filterChargingPointsBySource(state, { value, source }) {
    const locationSource = state.locationSources.find(locationSource => locationSource.type === source)

    if (!locationSource || locationSource.visibility === value) return

    locationSource.visibility = value

    state.chargingpoints = value
      ? [...state.chargingpoints, ...state.initialChargingpoints.filter(cp => getSourceType(cp) === source)]
      : state.chargingpoints.filter(cp => getSourceType(cp) !== source)

    storeSourceVisibility({ [source]: value })
  },
  setParkingLotIds(state, { ids }) {
    state.selectedParkingLotIds = ids
  },
  setHighlightedParkingLots(state, { ids }) {
    state.highlightedParkingLots = ids
  },
  setActiveState(state, { active }) {
    state.active = !!active
  },
  setIsPlanningAlternativeLocation(state, { active }) {
    state.isPlanningAlternativeLocation = active
  },
  setRejectedLocationUuid(state, { uuid }) {
    state.rejectedLocationUuid = uuid
  },
  setMapLinesById(state, { id, lines }) {
    state.mapLines = state.mapLines.filter(line => line.id !== id)

    lines.forEach(newLine => state.mapLines.push({
      id,
      ...newLine,
    }))
  },
  addFlaggedLocation(state, { uuid }) {
    state.flaggedLocations.add(uuid)
  },
  /**
   * Upon loading the Chargingpoints data layer, or switching municipalities, the chargingpoints are loaded / replaced
   */
  setChargingPoints(state, { chargingpoints }) {
    const chargingpointsBySource = new Map()

    chargingpoints.forEach(cp => {
      let source = cp.dataSource
      if (!source?.value) {
        source = { value: 'unknown', label: 'Unknown' }
      }
      if (!chargingpointsBySource.get(source?.value)) {
        chargingpointsBySource.set(source.value, { label: source.label, uuids: new Set() })
      }
      chargingpointsBySource.get(source.value).uuids.add(cp.uuid)
    })

    // The user's choice takes priority over the default
    const storedVisibility = getStoredSourceVisibility()

    state.locationSources = [...chargingpointsBySource
      .entries()
      .map(([source, values]) => ({
        type: source,
        label: values.label,
        count: values.uuids.size,
        visibility: storedVisibility[source] ?? !SOURCES_HIDDEN_BY_DEFAULT.includes(source),
      }))].sort((a, b) => a.label.localeCompare(b.label))

    const hiddenSources = new Set(state.locationSources.filter(source => !source.visibility).map(source => source.type))

    state.initialChargingpoints = chargingpoints
    state.chargingpoints = hiddenSources.size
      ? chargingpoints.filter(cp => !hiddenSources.has(getSourceType(cp)))
      : chargingpoints
  },
  setSelectedChargingPoint(state, { _id }) {
    state.selectedChargingPointID = _id
  },
  addChargingpoint(state, { chargingpoint }) {
    const isSourceHidden = state.locationSources.some(source => source.type === getSourceType(chargingpoint) && !source.visibility)

    // Points of a hidden source are added to the map once their source is shown again
    if (!isSourceHidden) {
      state.chargingpoints.push(chargingpoint)
    }

    // Keep the unfiltered list complete when locations are hidden by source
    if (state.initialChargingpoints !== state.chargingpoints) {
      upsertByUuid(state.initialChargingpoints, chargingpoint)
    }
  },
  updateChargingpoint(state, { chargingpoint, index }) {
    state.chargingpoints.splice(index, 1, chargingpoint)

    if (state.initialChargingpoints !== state.chargingpoints) {
      upsertByUuid(state.initialChargingpoints, chargingpoint)
    }
  },
  deleteChargingpoint(state, { chargingpointUuid }) {
    state.chargingpoints = state.chargingpoints.filter(
      (chargingpoint) => chargingpoint.uuid !== chargingpointUuid,
    )
    state.initialChargingpoints = state.initialChargingpoints.filter(
      (chargingpoint) => chargingpoint.uuid !== chargingpointUuid,
    )
  },

  updateComment(state, { comment, index }) {
    const _id = comment._id
    const allCommentsIndex = state.allComments.findIndex(com => com._id === _id)

    state.allComments.splice(index, 1, comment)
    state.allComments.splice(allCommentsIndex, 1, comment)
  },
  updateCommentsAsNeeded(state, { comments }) {
    const commentsById = _.keyBy(state.allComments, '_id')
    comments.forEach(comment => {
      commentsById[comment._id] = comment
    })

    state.allComments = Object.values(commentsById)

    // Prepare a new allComments array with upserts
    const allCommentsById = _.keyBy(state.allComments, '_id')
    comments.forEach(comment => {
      allCommentsById[comment._id] = comment
    })
    const newAllComments = Object.values(allCommentsById)

    // Only update if changed
    if (!_.isEqual(state.allComments, newAllComments)) {
      state.allComments = newAllComments
    }
  },
  setAllComments(state, { comments }) {
    state.allComments = comments
  },
  setAllCPOS(state, { allCPOS }) {
    state.allCPOS = allCPOS
  },
  addParticipationComment(state, { comment }) {
    state.participationComments.push(comment)
  },
  updateParticipationComment(state, { comment, index }) {
    state.participationComments.splice(index, 1, comment)
  },

  /**
   * Set the layer details (e.g. which types of charging points are visible)
   */
  setPlanmodeLayers(state, { layers }) {
    state.layers = layers
  },
  updatePlanmodeLayerVisibility(state, { value, id }) {
    const index = state.layers.findIndex(layer => layer.id === id)

    // Don't update the layer if it could not be found
    if (index === -1) {
      return
    }

    state.layers[index].visible = value
    // trigger update
    state.layers = [...state.layers]
  },
  setLoadingState(state, { value }) {
    state.hasLoadedChargingPoints = value
  },
  setLoadingStateComments(state, { value }) {
    state.hasLoadedComments = value
  },
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
